import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Award } from 'lucide-react';

// Curated preset palette — mirrors the backend whitelist in
// SOBRIETY_CLOCK_COLORS.  Each theme applies a background gradient to
// the wrapping box; interior text always stays white on top for
// consistent readability.
export const CLOCK_COLOR_THEMES = {
  default: 'bg-white/10 backdrop-blur-sm border border-white/20',
  ocean:   'bg-gradient-to-br from-sky-600/70 to-blue-800/70 border border-sky-300/30',
  emerald: 'bg-gradient-to-br from-emerald-600/70 to-teal-800/70 border border-emerald-300/30',
  sunset:  'bg-gradient-to-br from-orange-500/70 to-rose-700/70 border border-orange-300/30',
  violet:  'bg-gradient-to-br from-purple-600/70 to-indigo-800/70 border border-purple-300/30',
  rose:    'bg-gradient-to-br from-pink-500/70 to-rose-700/70 border border-pink-300/30',
  slate:   'bg-gradient-to-br from-slate-600/80 to-zinc-800/80 border border-slate-300/30',
  gold:    'bg-gradient-to-br from-amber-500/70 to-yellow-700/70 border border-amber-300/30',
};

export const CLOCK_COLOR_LABELS = {
  default: 'Default (translucent)',
  ocean:   'Ocean',
  emerald: 'Emerald',
  sunset:  'Sunset',
  violet:  'Violet',
  rose:    'Rose',
  slate:   'Slate',
  gold:    'Gold',
};

function SobrietyClock({ sobrietyDate, sobrietyTime, primaryDrugOfChoice, inline = false, colorTheme = 'default' }) {
  const [duration, setDuration] = useState({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [totalDays, setTotalDays] = useState(0);

  // Determine if alcohol is the primary drug - use "Sober Time", otherwise "Clean Time"
  const isAlcohol = primaryDrugOfChoice?.toLowerCase()?.includes('alcohol');
  const clockLabel = isAlcohol ? 'Sober Time' : 'Clean Time';
  const daysLabel = isAlcohol ? 'days sober' : 'days clean';

  useEffect(() => {
    if (!sobrietyDate) return;

    const calculateDuration = () => {
      // Default to midday LOCAL time when no time was provided —
      // users often don't recall the exact time of their last drink/drug.
      const startDateTime = sobrietyTime
        ? new Date(`${sobrietyDate}T${sobrietyTime}`)
        : new Date(`${sobrietyDate}T12:00:00`);
      
      const now = new Date();
      const diffMs = now - startDateTime;
      
      if (diffMs < 0) {
        setDuration({ years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 });
        setTotalDays(0);
        return;
      }

      const totalDaysCalc = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      setTotalDays(totalDaysCalc);

      const years = Math.floor(totalDaysCalc / 365);
      const months = Math.floor((totalDaysCalc % 365) / 30);
      const days = totalDaysCalc % 30;
      
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      setDuration({ years, months, days, hours, minutes, seconds });
    };

    calculateDuration();
    const interval = setInterval(calculateDuration, 1000);

    return () => clearInterval(interval);
  }, [sobrietyDate, sobrietyTime]);

  // Milestone badges
  const getMilestone = (days) => {
    if (days >= 365 * 5) return { label: '5+ Years', color: 'bg-purple-500', icon: '💎' };
    if (days >= 365) return { label: '1+ Year', color: 'bg-yellow-500', icon: '🏆' };
    if (days >= 180) return { label: '6 Months', color: 'bg-blue-500', icon: '⭐' };
    if (days >= 90) return { label: '90 Days', color: 'bg-green-500', icon: '🎯' };
    if (days >= 30) return { label: '30 Days', color: 'bg-teal-500', icon: '✨' };
    if (days >= 7) return { label: '1 Week', color: 'bg-indigo-500', icon: '🌟' };
    if (days >= 1) return { label: 'Day ' + days, color: 'bg-zinc-600', icon: '💪' };
    return { label: 'Day 1', color: 'bg-zinc-500', icon: '🌱' };
  };

  const milestone = getMilestone(totalDays);

  if (!sobrietyDate) {
    return (
      <div className={inline ? '' : 'bg-white border-2 border-black rounded-xl p-4'} data-testid="sobriety-clock-empty">
        <div className="text-center">
          <Clock className="w-8 h-8 text-black mx-auto mb-2" />
          <p className="text-black text-base">Set your sobriety date in your profile to start tracking</p>
        </div>
      </div>
    );
  }

  return (
    <div className={inline ? '' : 'bg-white border-2 border-black rounded-xl p-4'} data-testid="sobriety-clock">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-black" />
          <span className="text-black font-bold text-base">{clockLabel}</span>
        </div>
        <div className="bg-white border-2 border-black px-2 py-1 rounded-full flex items-center gap-1">
          <span className="text-base">{milestone.icon}</span>
          <span className="text-sm font-bold text-black">{milestone.label}</span>
        </div>
      </div>

      {/* Main Counter */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {duration.years > 0 && (
          <div className="bg-white border-2 border-black rounded-lg p-2 text-center">
            <div className="text-2xl font-bold text-black" data-arp="sobriety-count">{duration.years}</div>
            <div className="text-sm text-black">Years</div>
          </div>
        )}
        {(duration.years > 0 || duration.months > 0) && (
          <div className="bg-white border-2 border-black rounded-lg p-2 text-center">
            <div className="text-2xl font-bold text-black" data-arp="sobriety-count">{duration.months}</div>
            <div className="text-sm text-black">Months</div>
          </div>
        )}
        <div className="bg-white border-2 border-black rounded-lg p-2 text-center">
          <div className="text-2xl font-bold text-black" data-arp="sobriety-count">{duration.days}</div>
          <div className="text-sm text-black">Days</div>
        </div>
      </div>

      {/* Live Timer */}
      <div className="bg-white border-2 border-black rounded-lg p-3">
        <div className="flex items-center justify-center gap-1 font-mono">
          <div className="bg-white border-2 border-black px-2 py-1 rounded">
            <span className="text-xl font-bold text-black">{String(duration.hours).padStart(2, '0')}</span>
          </div>
          <span className="text-black text-xl">:</span>
          <div className="bg-white border-2 border-black px-2 py-1 rounded">
            <span className="text-xl font-bold text-black">{String(duration.minutes).padStart(2, '0')}</span>
          </div>
          <span className="text-black text-xl">:</span>
          <div className="bg-white border-2 border-black px-2 py-1 rounded">
            <span className="text-xl font-bold text-black animate-pulse">{String(duration.seconds).padStart(2, '0')}</span>
          </div>
        </div>
        <div className="text-center mt-2">
          <span className="text-base text-black">
            Total: <span className="font-bold text-black" data-arp="sobriety-count">{totalDays.toLocaleString()}</span> {daysLabel}
          </span>
        </div>
      </div>

      {/* Start Date - hidden in inline mode */}
      {!inline && (
        <div className="mt-3 flex items-center justify-center gap-2 text-black text-base">
          <Calendar className="w-3 h-3" />
          <span>Started: {new Date(sobrietyDate).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      )}
    </div>
  );
}

export default SobrietyClock;
